import type { DmThread } from "../app/services/dmThreads";

/**
 * オファー（subcontract）ワークフロー上での DM スレッドステータス判定ヘルパ。
 *
 * `new` / `contractor_pending` 等のステータスは見積もりフロー（master_contract）
 * など他フローでも利用されているため、必ず `thread_type === "subcontract"`
 * かつ viewer のロール（requester / contractor）と組み合わせて判定する。
 */

/**
 * subcontract のドラフト状態を表す API status 値。
 *
 * BE は ``contract-status-flow.md`` の S1 早見表に揃えて ``'draft'`` を返すが、
 * 旧 API 値の ``'new'`` がレスポンス・ローカル状態の双方で残存しているため、
 * FE では両方をドラフト扱いとする。判定箇所が増えるたびに二重管理しないよう、
 * このヘルパで統一する。
 */
export const isOfferDraftStatus = (
  status: string | null | undefined,
): boolean => status === "draft" || status === "new";

const isSubcontract = (thread: DmThread | null | undefined): thread is DmThread =>
  !!thread && thread.thread_type === "subcontract";

const isViewerRequester = (
  thread: DmThread,
  viewerUserId: number | undefined,
): boolean => !!viewerUserId && thread.requester?.id === viewerUserId;

const isViewerContractor = (
  thread: DmThread,
  viewerUserId: number | undefined,
): boolean => !!viewerUserId && thread.contractor?.id === viewerUserId;

/**
 * ドラフト（`status === "draft" | "new"`）かつ requester 本人 →
 * 右カラムにオファーウィザードを表示すべき状態。
 *
 * BE 仕様（dm-threads.md §可視性ルール）により、subcontract のドラフトは
 * requester にしか見えないので contractor 側で誤判定する心配はない。
 */
export const isOfferDraftingForViewer = (
  thread: DmThread | null | undefined,
  viewerUserId: number | undefined,
): boolean => {
  if (!isSubcontract(thread)) return false;
  if (!isOfferDraftStatus(thread.status)) return false;
  return isViewerRequester(thread, viewerUserId);
};

/**
 * `status === "contractor_pending"` かつ contractor 本人 → 受領モーダルを開くべき状態。
 */
export const isOfferReceivePendingForViewer = (
  thread: DmThread | null | undefined,
  viewerUserId: number | undefined,
): boolean => {
  if (!isSubcontract(thread)) return false;
  if (thread.status !== "contractor_pending") return false;
  return isViewerContractor(thread, viewerUserId);
};

/**
 * 契約書アイテム（タイムライン上の `contract_uploaded` カード）の状態。
 *
 *  - `pre`       : 締結前（受信者の引受待ち）
 *  - `post`      : 締結後（検収前）
 *  - `inspected` : 検収後
 *  - `invalid`   : 無効化（`rejected` / `closed`）
 *
 * 状態は `thread.status_timestamps` を優先して判定し、未設定の場合のみ `status` を見る。
 * これにより `contract_concluded` system メッセージが BE から飛ばなくても、
 * Thread 側の遷移日時のみで安定して切り替えられる。
 */
export type ContractCardPhase = "pre" | "post" | "inspected" | "invalid";

export const getContractCardPhase = (
  thread: DmThread | null | undefined,
): ContractCardPhase => {
  if (!thread) return "pre";
  if (thread.status === "rejected" || thread.status === "closed") {
    return "invalid";
  }
  if (thread.status === "completed" || thread.status_timestamps.inspected_at) {
    return "inspected";
  }
  // 受諾済み（in_progress / inspection_pending）は契約成立後フェーズ。
  // contracted_at が未正規化の環境でも status で確実に判定する。
  if (
    thread.status === "in_progress" ||
    thread.status === "inspection_pending" ||
    thread.status_timestamps.contracted_at
  ) {
    return "post";
  }
  return "pre";
};
